import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import { format } from "date-fns";

import "react-datepicker/dist/react-datepicker.css";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "policy_advisory_role";

const PolicyAdvisoryRole = () => {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [recordId, setRecordId] = useState(null);

  const [formData, setFormData] = useState({
    starting_date: null,
    ending_date: null,
    role_name: "",
    entity_name: "",
    entity_nature: "",
    country: "",
  });

  // ---------- Fetch Records ----------
  const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = (Array.isArray(data) ? data : [])
      .map((r) => ({
        ...r,
        starting_date: r.starting_date
          ? new Date(r.starting_date)
          : null,
        ending_date: r.ending_date
          ? new Date(r.ending_date)
          : null,
      }))
      .sort((a, b) => {
        if (!a.starting_date) return 1;
        if (!b.starting_date) return -1;

        return b.starting_date.getTime() - a.starting_date.getTime();
      });

    setRecords(processed);

  } catch (err) {
    toast.error(`Fetch error: ${err.message}`);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  // ---------- Helpers ----------
  const convertToSQLDate = (dateObj) => {
    if (!dateObj) return null;
    const y = dateObj.getFullYear();
    const m = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    return `${y}-${m}-01`;
  };

  const formatDate = (date) => (date ? format(new Date(date), "yyyy-MM") : "");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      starting_date: convertToSQLDate(formData.starting_date),
      ending_date: convertToSQLDate(formData.ending_date),
    };

    try {
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;

      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success("Record saved successfully!");

      setFormData({
        starting_date: null,
        ending_date: null,
        role_name: "",
        entity_name: "",
        entity_nature: "",
        country: "",
      });

      setRecordId(null);
      fetchRecords();
    } catch (err) {
      toast.error(`Submit error: ${err.message}`);
    }
  };

  // ---------- Edit ----------
  const handleEdit = (rec) => {
    setFormData({
      starting_date: rec.starting_date ? new Date(rec.starting_date) : null,
      ending_date: rec.ending_date ? new Date(rec.ending_date) : null,
      role_name: rec.role_name || "",
      entity_name: rec.entity_name || "",
      entity_nature: rec.entity_nature || "",
      country: rec.country || "",
    });
    setRecordId(rec.id);
    toast.info("Editing record...");
  };

  // ---------- Delete ----------
  const handleDelete = (rec) => {
    const toastId = toast.info(
      <div>
        Delete this record?
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${rec.id}`,
                { method: "DELETE", credentials: "include" }
              );
              if (!res.ok) throw new Error("Failed to delete");

              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted!",
                type: "success",
                autoClose: 2000,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 2000,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false }
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />

      <div
        style={{
          maxWidth: "850px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2 style={{ textAlign: "center", color: "#1e3a8a" }}>
          Policy Advisory Roles
        </h2>

        {/* ---------- Form ---------- */}
        <form onSubmit={handleSubmit}>
          <label style={{ fontWeight: "bold" }}>
            Duration (From mm/yyyy – To mm/yyyy)
          </label>

          <div style={{ display: "flex", gap: "10px", marginTop: "5px" }}>
            <DatePicker
              selected={formData.starting_date}
              onChange={(d) => setFormData({ ...formData, starting_date: d })}
              dateFormat="yyyy-MM"
              showMonthYearPicker
              placeholderText="From"
              className="form-control"
            />
            <DatePicker
              selected={formData.ending_date}
              onChange={(d) => setFormData({ ...formData, ending_date: d })}
              dateFormat="yyyy-MM"
              showMonthYearPicker
              placeholderText="To"
              className="form-control"
            />
          </div>

          {[
            { label: "Name of the Role", name: "role_name" },
            { label: "Name of the Entity / Organization", name: "entity_name" },
          ].map((f) => (
            <div key={f.name}>
              <label style={{ fontWeight: "bold", marginTop: "10px" }}>
                {f.label}
              </label>
              <input
                type="text"
                name={f.name}
                value={formData[f.name]}
                onChange={handleChange}
                required
                style={{
                  padding: "10px",
                  marginTop: "5px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  width: "100%",
                }}
              />
            </div>
          ))}

          <label style={{ fontWeight: "bold", marginTop: "10px" }}>
            Nature of the Entity / Organization
          </label>
          <select
            name="entity_nature"
            value={formData.entity_nature}
            onChange={handleChange}
            required
            style={{
              padding: "10px",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              width: "100%",
            }}
          >
            <option value="">Select</option>
            <option value="Public">Public</option>
            <option value="Private">Private</option>
          </select>

          <label style={{ fontWeight: "bold", marginTop: "10px" }}>
            Country
          </label>
          <input
            type="text"
            name="country"
            value={formData.country}
            onChange={handleChange}
            required
            style={{
              padding: "10px",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "6px",
              width: "100%",
            }}
          />

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* ---------- Table ---------- */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[
                    "From",
                    "To",
                    "Role",
                    "Entity",
                    "Nature",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r) => (
                  <tr key={r.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatDate(r.starting_date)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatDate(r.ending_date)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {r.role_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {r.entity_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {r.entity_nature}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {r.country}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        onClick={() => handleEdit(r)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          marginRight: "5px",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(r)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default PolicyAdvisoryRole;
